import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

interface ProductAccess {
  workflows: boolean;
  incident_response: boolean;
  copilot: boolean;
}

interface SubscriptionContextValue {
  isProductBilling: boolean;
  access: ProductAccess;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  isProductBilling: false,
  access: { workflows: true, incident_response: true, copilot: true },
  loading: true,
  refresh: async () => {},
});

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [isProductBilling, setIsProductBilling] = useState(false);
  const [access, setAccess] = useState<ProductAccess>({ workflows: true, incident_response: true, copilot: true });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [status, subscriptions] = await Promise.all([
        apiClient.getSubscriptionStatus(),
        apiClient.getBillingSubscriptions().catch(() => []),
      ]);

      const isProduct = status?.is_product_subscription === true;
      setIsProductBilling(isProduct);

      if (!isProduct) {
        setAccess({ workflows: true, incident_response: true, copilot: true });
      } else {
        const hasAccess = (product: string): boolean => {
          const sub = subscriptions.find((s: any) => s.product === product);
          if (!sub) return false;
          if (sub.status === 'active') return true;
          if (sub.status === 'trial') {
            const trialEnd = sub.trial_ends_at ? new Date(sub.trial_ends_at) : null;
            return trialEnd ? trialEnd > new Date() : true;
          }
          return false;
        };

        setAccess({
          workflows: hasAccess('workflows'),
          incident_response: hasAccess('incident_response'),
          copilot: hasAccess('copilot'),
        });
      }
    } catch {
      setAccess({ workflows: true, incident_response: true, copilot: true });
      setIsProductBilling(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for subscription-changed events (fired by billing page on subscribe/cancel)
  useEffect(() => {
    const handler = () => { refresh(); };
    window.addEventListener('subscription-changed', handler);
    return () => window.removeEventListener('subscription-changed', handler);
  }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ isProductBilling, access, loading, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
